import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { BACKEND_URL, authHeaders } from '../utils/backend';

/**
 * Dashboard.
 *
 * Rewritten because the old version could not work: it called
 * /api/summary-statistics, /knowledge-base/stats and /knowledge-base/score-trends on
 * http://localhost:8000. None of those paths exist on the live Fashion Buyer API, and
 * the cards read fields (total_uploads, average_machine_score, domain_counts) that the
 * API never returns.
 *
 * It now shows what the API actually has: collection and look counts, brand counts,
 * rating aggregates, and the rating distribution. Card titles were changed to match the
 * data rather than mapping invented values onto the old labels.
 */

const COLORS = ['#4F8EF7', '#34C759', '#FFB300', '#FF7F0E', '#B0B0B0'];

function Card({ title, value }) {
  const display = value === null || value === undefined || Number.isNaN(value) ? '—' : value;
  return (
    <div className="bg-white rounded-lg shadow p-6 flex flex-col items-center justify-center min-h-[120px]">
      <div className="text-gray-500 text-sm mb-1">{title}</div>
      <div className="text-2xl font-bold">{display}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

async function getJSON(path, headers) {
  const res = await fetch(`${BACKEND_URL}${path}`, { headers });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
}

export default function Summary() {
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const headers = await authHeaders();
        const [o, s, t] = await Promise.all([
          getJSON('/analytics/overview', headers),
          getJSON('/collections/statistics', headers),
          getJSON('/analytics/trends', headers),
        ]);
        if (!alive) return;
        setOverview(o); setStats(s); setTrends(t); setError(null);
      } catch (e) {
        if (alive) setError(e.message || 'Could not load the dashboard');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="p-4 max-w-5xl mx-auto"><Spinner /></div>;

  const distribution = (trends && trends.rating_distribution) || [];
  const pieData = distribution
    .filter((d) => d.count > 0)
    .map((d, i) => ({ name: `Score ${d.score}`, value: d.count, color: COLORS[i % COLORS.length] }));
  const lineData = distribution.map((d) => ({ index: d.score, count: d.count }));

  return (
    <div className="p-4 max-w-5xl mx-auto animate-fade-in">
      <h1 className="text-2xl font-bold mb-2">Summary</h1>
      <p className="text-sm text-gray-500 mb-6">Live counts from the Fashion Buyer API.</p>

      {error && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          Some figures could not be loaded: {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <Card title="Total Looks" value={stats?.total_looks} />
        <Card title="Collections" value={stats?.total_collections} />
        <Card title="Reviewed Collections" value={stats?.reviewed_collections} />
        <Card title="Rated Looks" value={stats?.rated_looks} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <Card title="Unique Brands" value={stats?.unique_brands} />
        <Card title="Average Rating" value={overview?.avg_rating} />
        <Card title="Total Ratings" value={overview?.total_ratings} />
        <Card title="Active Days" value={overview?.active_days} />
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Rating Distribution</h2>
        {pieData.length ? (
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} isAnimationActive={true}>
                  {pieData.map((entry, idx) => (<Cell key={`cell-${idx}`} fill={entry.color} />))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No ratings recorded yet.</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Looks per Rating Band</h2>
        {lineData.length ? (
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={lineData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="index" tick={{ fontSize: 12 }} label={{ value: 'Score', position: 'insideBottom', offset: -5 }} />
                <YAxis tick={{ fontSize: 12 }} label={{ value: 'Looks', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => [value, 'Looks']} />
                <Line type="monotone" dataKey="count" name="Looks" stroke="#4F8EF7" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No rating data yet.</p>
        )}
      </div>
    </div>
  );
}
